import {
  Character,
  Creature,
  Encounter,
  EncounterParticipant,
  TrueCreatureInstance,
} from "@dndModels";

import API from "@utilities/smart_api";
import CharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

const api = API.getInstance();
const char_util = CharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export interface AddingCreature {
  creature: Creature;
  count: number;
  color: string;
  cr?: number; // For scalable creatures, otherwise it uses the creature's CR
}

// Adds each creature as a participant (with count instances under it) the same way AddParticipants does.
export const addCreatureParticipants = async (encounter: Encounter, adding: AddingCreature[]) => {
  let add_time = common_util.get_time_number();
  for (const { creature, count, color, cr } of adding) {
    await api.loadObject(creature);
    let in_encounter = encounter.participants.filter(o => o.creature_id === creature._id)
      .reduce((total, o) => total + Math.max(o.child_participants.length, 1), 0);
    const creature_instances: TrueCreatureInstance[] = [];
    for (let i = 0; i < count; ++i) {
      const new_creature_inst = await char_util.creatureToCreatureInstance({ creature, cr });
      new_creature_inst._num = in_encounter + 1;
      new_creature_inst.name = new_creature_inst.name + (in_encounter > 0 ? ` ${in_encounter + 1}` : "");
      await char_util.buildMind(new_creature_inst, true);
      in_encounter++;
      creature_instances.push(new_creature_inst);
    }
    const new_participant = new EncounterParticipant();
    new_participant.participant = creature_instances[0];
    new_participant.participant_id = creature_instances[0].true_id;
    new_participant.child_participants = creature_instances;
    new_participant.creature_id = creature._id;
    new_participant.owner_id = encounter.owner_id;
    new_participant.owner_name = api.user ? api.user.name : "";
    new_participant.participant_type = "Creature";
    new_participant.color = color;
    new_participant.add_time = add_time;
    add_time++;
    encounter.participants.push(new_participant);
    if (creature_instances[0].lair_actions.length > 0 &&
      encounter.participants.filter(o => o.participant_type === "Lair Actions").length === 0) {
      const lair_actions_participant = new EncounterParticipant();
      lair_actions_participant.initiative = 20;
      lair_actions_participant.participant_id = "Lair Actions";
      lair_actions_participant.owner_id = encounter.owner_id;
      lair_actions_participant.owner_name = api.user ? api.user.name : "";
      lair_actions_participant.participant_type = "Lair Actions";
      lair_actions_participant.add_time = add_time;
      add_time++;
      encounter.participants.push(lair_actions_participant);
    }
  }
};

// Adds characters as participants the same way AddCharacterParticipants does, skipping any already in it.
export const addCharacterParticipants = (encounter: Encounter, characters: Character[], color: string) => {
  let add_time = common_util.get_time_number();
  for (const character of characters) {
    if (!encounter.participants.some(o => o.participant_type === "Character" && o.participant_id === character._id)) {
      const new_participant = new EncounterParticipant();
      new_participant.participant = character;
      new_participant.participant_id = character._id;
      new_participant.owner_id = character.owner_id;
      new_participant.participant_type = "Character";
      new_participant.color = color;
      new_participant.add_time = add_time;
      add_time++;
      encounter.participants.push(new_participant);
    }
  }
};
